import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserTypeInfo } from '../models/user.model';
import { map } from 'rxjs/operators';
import { ApiResponse } from '../models/response.model';
import { BankResponse, BankStatement, ExperianScore } from '../models/bank.model';
import { AddressProofType, Kyc } from '../models/kyc.model';
import { OnboardingStepResponse } from '../models/onboarding.step.model';

@Injectable({
  providedIn: 'root',
})
export class KycService {

  _profileEndpoint = `${environment.apiBaseUrl}/user/v1/customer/`;

  constructor(
    private _http: HttpClient
  ) { }

  getUserKyc(): Observable<Kyc> {
    return this._http.get<ApiResponse<any>>(this._profileEndpoint + 'kyc')
      .pipe(map((res) => res.data));
  }

  getKycOnboardingSteps(): Observable<OnboardingStepResponse> {
    return this._http.get<ApiResponse<any>>(`${this._profileEndpoint}kyc/onboarding-steps`)
      .pipe(map((res) => res.data));
  }

  getSubscriptionKycOnboardingSteps(subscriptionId: string): Observable<OnboardingStepResponse> {
    return this._http.get<ApiResponse<any>>(`${this._profileEndpoint}kyc/onboarding-steps/${subscriptionId}`)
      .pipe(map((res) => res.data));
  }

  getKycOnboardingStepsBySubscriptionId(subscriptionId: string): Observable<OnboardingStepResponse> {
    return this._http.get<ApiResponse<any>>(this._profileEndpoint + 'kyc/onboarding-steps/' + subscriptionId)
      .pipe(map((res) => res.data));
  }

  addWorkInfo(params: any): Observable<UserTypeInfo> {
    return this._http.patch<ApiResponse<any>>(this._profileEndpoint + 'kyc/work-info', params)
      .pipe(map((res) => res.data));
  }

  addAdditionalInfo(params: any): Observable<any> {
    return this._http.patch<ApiResponse<any>>(this._profileEndpoint + 'kyc/additional-details', params)
      .pipe(map((res) => res.data));
  }

  addAdditionalDocuments(params: any): Observable<any> {
    return this._http.patch<ApiResponse<any>>(this._profileEndpoint + 'kyc/additional-documents', params)
      .pipe(map((res) => res.data));
  }

  verifyExperianOtp(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'verify-experion-otp', params)
      .pipe(map((res) => res.data));
  }

  generateMaskedMobileOTP(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'generate-masked-otp', params)
      .pipe(map((res) => res.data));
  }

  verifyMaskedMobileOTP(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'verify-masked-otp', params)
      .pipe(map((res) => res.data));
  }

  getAddressProofTypes(current: boolean = false): AddressProofType[] {
    return [
      ...(current ? [
        {
          label: 'Rent Agreement',
          value: 'rent_agreement',
        },
      ] : []),
      {
        label: 'Water Bill',
        value: 'water_bill',
      },
      {
        label: 'Electricity Bill',
        value: 'electricity_bill',
      },
      {
        label: 'Aadhar Card',
        value: 'aadhar_card',
      },
    ];
  }

  updateAddress(params: any): Observable<any> {
    return this._http.patch<ApiResponse<any>>(this._profileEndpoint + 'kyc/address', params)
      .pipe(map((res) => res));
  }

  verifyPan(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'kyc/pan', params)
      .pipe(map((res) => res));
  }

  verifyDl(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'kyc/dl', params)
      .pipe(map((res) => res));
  }

  uploadBankStatement(subscriptionId: string, params: FormData): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'credit/bank-statement/' + subscriptionId, params)
      .pipe(map((res) => res));
  }

  getBankStatements(): Observable<any> {
    return this._http.get<ApiResponse<any>>(this._profileEndpoint + 'credit/bank-statement')
      .pipe(map((res) => res.data));
  }

  sendAadharOtp(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'kyc/adhar-otp', params)
      .pipe(map((res) => res));
  }

  sendContactOtp(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'kyc/adhar-otp', params)
      .pipe(map((res) => res));
  }

  verifyAadharOtp(params: any): Observable<any> {
    return this._http.post<ApiResponse<any>>(this._profileEndpoint + 'kyc/verify-adhar-otp', params)
      .pipe(map((res) => res));
  }

  getBusinessTypes(): Observable<string[]> {
    return this._http.get<ApiResponse<any>>(this._profileEndpoint + 'kyc/business-types')
      .pipe(map((res) => res.data));
  }

  getBanks(): Observable<BankResponse> {
    return this._http.get<ApiResponse<any>>(this._profileEndpoint + 'credit/banks')
      .pipe(map((res) => res.data));
  }

  getCreditLimit(): Observable<ExperianScore> {
    return this._http.get<ApiResponse<any>>(this._profileEndpoint + 'credit/limit')
      .pipe(map((res) => res.data));
  }

}